import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import LibraryLearningVelocityTile from "../../components/app/LibraryLearningVelocityTile";
import AutopilotStatusTile from "../../components/app/AutopilotStatusTile";
import OnboardingBanner from "../../components/app/OnboardingBanner";
import BudgetStatusCards from "../../components/app/BudgetStatusCards";
import CockpitTiles from "../../components/app/CockpitTiles";
import FirstCampaignConcierge from "../../components/app/FirstCampaignConcierge";

export default function Dashboard() {
  const t = useT();
  const [runs, setRuns] = useState([]);
  const [flags, setFlags] = useState([]);
  const [agents, setAgents] = useState([]);
  const [retry, setRetry] = useState(null);
  const [securityDenies, setSecurityDenies] = useState(null);
  const [campaignsCount, setCampaignsCount] = useState(null);

  useEffect(() => {
    api.get("/agents/runs?limit=8").then((r) => setRuns(r.data.items || [])).catch(() => {});
    api.get("/feature-flags").then((r) => setFlags(r.data.flags || [])).catch(() => {});
    api.get("/agents/catalog").then((r) => setAgents(r.data.items || [])).catch(() => {});
    api.get("/tenants/me/retry-stats").then((r) => setRetry(r.data)).catch(() => {});
    api.get("/campaigns?limit=1")
      .then((r) => setCampaignsCount(r.data?.total ?? (r.data?.items || []).length))
      .catch(() => setCampaignsCount(0));
    // Phase 2A — Security activity (permission denials in last 7d)
    api.get("/audit/events", {
      params: { action: "identity.permission_denied", limit: 100 },
    }).then((r) => {
      const items = r.data.items || [];
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const recent = items.filter((e) => {
        const t = e.created_at ? new Date(e.created_at).getTime() : 0;
        return t >= cutoff;
      });
      setSecurityDenies({ total: recent.length, latest: recent.slice(0, 3) });
    }).catch(() => setSecurityDenies({ total: 0, latest: [] }));
  }, []);

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <OnboardingBanner />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t("dashboard.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("dashboard.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Active agents" value={agents.filter((a) => a.status === "active").length} data-testid="kpi-active-agents" />
        <Kpi label="Registered agents" value={agents.length} data-testid="kpi-registered-agents" />
        <Kpi label="Agent runs (24h)" value={runs.length} data-testid="kpi-recent-runs" />
        <Kpi label="Feature flags on" value={flags.filter((f) => f.enabled).length} data-testid="kpi-flags-on" />
      </div>

      <LibraryLearningVelocityTile />

      <BudgetStatusCards />

      <CockpitTiles />

      {campaignsCount === 0 && <FirstCampaignConcierge />}

      <AutopilotStatusTile />

      {retry && (
        <Card data-testid="retry-stats-card">
          <CardHeader><CardTitle className="text-base">Research reliability (7 days)</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Kpi label="Retried" value={retry.reports_retried_7d} data-testid="kpi-retried" />
              <Kpi label="Recovered" value={retry.recovered_7d} data-testid="kpi-recovered" />
              <Kpi label="Parse-failure rate" value={`${(retry.parse_failure_rate_7d * 100).toFixed(1)}%`} data-testid="kpi-parse-failure-rate" />
            </div>
          </CardContent>
        </Card>
      )}

      {securityDenies && (
        <Card data-testid="security-activity-card">
          <CardHeader>
            <CardTitle className="text-base">Security activity (7 days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
              <Kpi
                label="Permission denials"
                value={securityDenies.total}
                data-testid="kpi-security-denies"
              />
              <div className="md:col-span-2 rounded-md border p-4 text-xs space-y-2 bg-muted/20">
                <div className="uppercase tracking-wider text-muted-foreground">Latest 3 events</div>
                {securityDenies.latest.length === 0 ? (
                  <div className="text-muted-foreground">No denied requests in the last 7 days. Looking good.</div>
                ) : (
                  securityDenies.latest.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 font-mono text-[11px]" data-testid={`security-event-${i}`}>
                      <span className="text-muted-foreground">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</span>
                      <span>·</span>
                      <span>{(e.after || {}).role || "?"}</span>
                      <span>→</span>
                      <span>{(e.after || {}).required_permission || "?"}</span>
                      <span className="text-muted-foreground truncate">{(e.after || {}).route || ""}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="recent-runs-card">
        <CardHeader>
          <CardTitle className="text-base">{t("dashboard.recent_runs")}</CardTitle>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("dashboard.empty_kpi")}</p>
          ) : (
            <div className="space-y-2">
              {runs.map((r) => (
                <div key={r.id} className="flex items-center justify-between border rounded-md px-3 py-2 text-sm" data-testid={`run-row-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-muted-foreground">{(r.id || "").slice(0, 8)}</span>
                    <span className="font-medium">{r.agent_key}</span>
                  </div>
                  <Badge variant={r.status === "succeeded" ? "default" : r.status === "failed" ? "destructive" : "secondary"}>
                    {r.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, value, ...rest }) {
  return (
    <Card {...rest}>
      <CardContent className="pt-6">
        <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
        <div className="mt-2 text-3xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}
